import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Layout";
import { PageHeader, Breadcrumbs, Overline } from "@/components/Bits";
import api from "@/lib/api";
import { UtensilsCrossed, Sparkles, HardHat, Truck, Coffee, HeartHandshake, ShoppingBag, Building2, ArrowRight, ShieldAlert, MessageSquare } from "lucide-react";

const ICONS = { hosteleria: UtensilsCrossed, limpieza: Sparkles, construccion: HardHat, logistica: Truck, restauracion: Coffee, cuidados: HeartHandshake, comercio: ShoppingBag, oficinas: Building2 };

export function WorkGerman() {
  const [sectors, setSectors] = useState([]);
  useEffect(() => { api.get("/sectors").then(({ data }) => setSectors(data)); }, []);
  return (
    <>
      <Seo title="Alemán para trabajar" description="Vocabulario profesional, frases habituales, seguridad y simulaciones de entrevista por sector para trabajar en Suiza." />
      <PageHeader overline="Alemán para trabajar" title="El alemán de tu sector, no el del libro de texto"
        subtitle="Vocabulario, frases habituales, instrucciones y seguridad organizados por sector para tu día a día laboral en Suiza." />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Alemán para trabajar" }]} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {sectors.map((s) => {
            const Icon = ICONS[s.slug] || Building2;
            return (
              <Link key={s.slug} to={`/sectores/${s.slug}`} data-testid={`sector-${s.slug}`}
                className="group rounded-lg border border-border bg-card p-6 hover:-translate-y-1 hover:border-primary/40 transition-transform">
                <Icon size={24} className="text-primary mb-4" />
                <h2 className="font-display font-semibold text-lg flex items-center justify-between">{s.title}<ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" /></h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.intro}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function SectorDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/sectors/${slug}`).then(({ data }) => setS(data)); }, [slug]);
  if (!s) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  const Icon = ICONS[s.slug] || Building2;
  return (
    <>
      <Seo title={`Alemán para ${s.title.toLowerCase()}`} description={s.intro} />
      <PageHeader overline="Alemán para trabajar" title={s.title} subtitle={s.intro}>
        <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-1.5 text-xs text-muted-foreground">
          <ShieldAlert size={14} /> Vocabulario práctico para empezar. Contrasta los términos técnicos con tu empresa o fuentes oficiales del sector.
        </div>
      </PageHeader>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Alemán para trabajar", to: "/aleman-para-trabajar" }, { label: s.title }]} />
        <section>
          <Overline>Vocabulario profesional</Overline>
          <div className="grid sm:grid-cols-2 gap-2">{s.vocab.map((v, i) => <div key={i} className="rounded-md border border-border bg-card p-4"><p className="font-display font-semibold">{v.de}</p><p className="text-sm text-muted-foreground">{v.es}</p></div>)}</div>
        </section>
        <div className="grid sm:grid-cols-3 gap-6">
          {[["Frases habituales", s.phrases, MessageSquare], ["Instrucciones frecuentes", s.instructions, ArrowRight], ["Seguridad y comunicación", s.safety, ShieldAlert]].map(([title, list, IC]) => (
            <section key={title} className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-center gap-2 mb-3"><IC size={16} className="text-accent" /><h3 className="font-display font-semibold text-sm">{title}</h3></div>
              <ul className="space-y-2 text-sm">{list.map((x, i) => <li key={i} className="font-medium">{x}</li>)}</ul>
            </section>
          ))}
        </div>
        <div className="rounded-lg border-2 border-primary p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div><Icon size={24} className="text-primary mb-2" /><h3 className="font-display font-bold text-xl">¿Preparas una entrevista?</h3><p className="text-sm text-muted-foreground">Practica con nuestro simulador de conversaciones.</p></div>
          <Link to="/conversaciones"><Button className="rounded-full">Simular entrevista <ArrowRight size={16} className="ml-1" /></Button></Link>
        </div>
      </div>
    </>
  );
}
